import React from "react";
import isEmpty from "lodash/isEmpty";
import { useRouter } from "next/router";
import { Loader as CardsPlaceHolder } from "@gonoodle/gn-universe-ui";
import { invariant } from "@gonoodle/gn-universe-utils";
import { get } from "lodash";
import { SECTIONS_TYPES } from "@gonoodle/gn-universe-analytics-schema/src/constants";

import { useDataLoader } from "../../index";
import VideoTile from "../../../VideoTile";
import PrintableTile from "../../../PrintableTile";
import TextTile from "../../../TextTile";
import CustomLinkCardTile from "../../../CustomLinkCardTile";
import BlogCardTile from "../../../BlogCardTile";
import { CONTENT_TYPES } from "../../../../constants";
import { getSourcePageAndSourcePageTypeFromReferrer } from "../../../../utils/analytics";

export default function CardList({ templateOptions, fetchers }) {
  const { title, displayTitle, description } = templateOptions;
  const [fetcher] = fetchers;
  const { data, isLoading } = useDataLoader(fetcher);
  const { contentType, content: cards } = get(data, "[0]", {});
  const numberOfCards = cards?.length;
  let gridColumnsClasses = undefined;

  invariant(
    !(isLoading === false && (numberOfCards < 1 || numberOfCards > 5)),
    `CardList: Invalid number of cards expected between 1 and 5 got ${numberOfCards}`,
  );

  if (numberOfCards === 1) {
    gridColumnsClasses = "grid-cols-1";
  } else if (numberOfCards === 2) {
    gridColumnsClasses = "md:grid-cols-2";
  } else if (numberOfCards === 3) {
    gridColumnsClasses = "md:grid-cols-2 lg:grid-cols-3";
  } else if (numberOfCards === 4) {
    gridColumnsClasses = "md:grid-cols-2 lg:grid-cols-4";
  } else if (numberOfCards === 5) {
    gridColumnsClasses = "grid-cols-2 lg:grid-cols-3 xl:grid-cols-5";
  }

  if (isLoading) {
    return (
      <div className="grid gap-x-md gap-y-md md:grid-cols-2 lg:grid-cols-4">
        <CardsPlaceHolder cols={4} size="short" mode="dark" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="gn-text font-bold text-gray-200 capitalize mb-1">
        {displayTitle || title}
      </h2>

      <p className="gn-text-sm text-white mb-5">{description}</p>

      <ul className={`grid gap-x-md gap-y-md ${gridColumnsClasses}`}>
        {cards.map((card, index) =>
          contentType === CONTENT_TYPES.TAGS ||
          [
            CONTENT_TYPES.TOPIC,
            CONTENT_TYPES.CHANNEL,
            CONTENT_TYPES.TAG,
          ].includes(card.contentType) ? (
            <TextListItem
              key={`${card.id}_${index}`}
              card={card.content ? card.content : card}
              format={card.content ? "landscape" : "square"}
              title={title}
            />
          ) : contentType === CONTENT_TYPES.VIDEOS ||
            card.contentType === CONTENT_TYPES.VIDEO ? (
            <VideoListItem
              key={`${card.id}_${index}`}
              card={card.content || card}
              title={title}
            />
          ) : contentType === CONTENT_TYPES.ACTIVITIES ||
            card.contentType === CONTENT_TYPES.ACTIVITY ? (
            <PrintableListItem
              key={`${card.id}_${index}`}
              card={card.content || card}
              format={card.content ? "landscape" : "portrait"}
              title={title}
            />
          ) : card.contentType === CONTENT_TYPES.CUSTOM_LINK ? (
            <LinkCardListItem
              key={`${card.id}_${index}`}
              card={card.content}
              title={title}
            />
          ) : card.contentType === CONTENT_TYPES.BLOG_POST ? (
            <BlogPostListItem
              key={`${card.id}_${index}`}
              card={card.content}
              title={title}
            />
          ) : null,
        )}
      </ul>
    </div>
  );
}

function TextListItem({ card, format, title }) {
  const router = useRouter();
  const tile =
    card.image || card.images?.spotlightImage || card.images?.tileImage;
  return (
    <li className="flex flex-col justify-top relative">
      <TextTile
        id={card.id}
        slug={card.slug}
        name={card.name}
        title={card.name}
        description={card.description}
        tile={tile}
        format={format}
        linkParams={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
      />
    </li>
  );
}

function VideoListItem({ card, title }) {
  const router = useRouter();
  return (
    <li className="flex flex-col justify-top relative group">
      <VideoTile
        id={card.id}
        slug={card.slug}
        title={card.title}
        description={card.description}
        images={card.images}
        duration={card.duration}
        sponsor={{
          expiration: card.attribution?.expiration,
          type: card.attribution?.type,
        }}
        impressionsProps={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
        linkParams={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
      />
    </li>
  );
}

function PrintableListItem({ card, title, format }) {
  const router = useRouter();
  const sources =
    format === "landscape" && !isEmpty(card.heroImage)
      ? {
          "regular@1x": card.heroImage["thumb@1x"],
          "regular@2x": card.heroImage["thumb@2x"],
          "regular@3x": card.heroImage["thumb@3x"],
        }
      : card.image;
  return (
    <li className="flex flex-col justify-top relative group">
      <PrintableTile
        id={card.id}
        slug={card.slug}
        mode="dark"
        sources={sources}
        title={card.title}
        description={card.description}
        format={format}
        linkParams={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
      />
    </li>
  );
}

function LinkCardListItem({ card, title }) {
  const router = useRouter();
  const {
    id,
    slug,
    name,
    description,
    image,
    images,
    title: customLinkTitle,
    destinationUrl,
  } = card;
  return (
    <li className="flex flex-col justify-top relative group">
      <CustomLinkCardTile
        id={id}
        slug={slug}
        sources={images?.spotlightImage || image}
        title={name}
        name={name}
        customLinkTitle={customLinkTitle}
        destinationUrl={destinationUrl}
        description={description}
        format="landscape"
        linkParams={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
      />
    </li>
  );
}

function BlogPostListItem({ card, title }) {
  const router = useRouter();
  const { slug, title: blogTitle, description, images } = card;
  return (
    <li className="flex flex-col justify-top relative group">
      <BlogCardTile
        slug={slug}
        title={title}
        name={blogTitle}
        description={description}
        imageUrl={images.tile}
        analyticsProps={{
          sourceElement: SECTIONS_TYPES.SHELF,
          sourceName: title,
          ...getSourcePageAndSourcePageTypeFromReferrer(
            router.pathname,
            router.query,
          ),
        }}
      />
    </li>
  );
}
